import { Injectable } from '@angular/core';
import { SdkBase } from '../utilities/sdk-base/sdk.base';
import { ODataRequest } from '../utilities/sdk-base/o-data-request';
import { UploadBatchResource } from '../models/resource/upload-batch.resource';
import { forkJoin, map, Observable, of } from 'rxjs';
import { Upload } from '../models/ui/upload.model';
import { Collection } from '../models/ui/collection.model';
import { UploadBatchResourceToUploadMapper } from '../models/mappers/upload-batch-resource-to-upload.mapper';
import { PagingResponse } from '@models/paging-response.model';
import { UploadToUploadBatchResourceMapper } from '../models/mappers/upload-to-upload-batch-resource.mapper';

@Injectable({
	providedIn: 'root'
})
export class UploadBatchesSdk extends SdkBase {
	public get(params: { skip: number, take: number, orderByColumn: string, orderByDirection: 'asc' | 'desc' }): Observable<Collection<Upload>>
	public get(params: { id: string }): Observable<Upload>
	public get(params?: GetParams): Observable<Collection<Upload> | Upload> {
		if (params?.id) {
			const request = new ODataRequest('get', `uploadbatches/${ params.id }`);
			return this.makeODataRequest$<UploadBatchResource>(request, 'api2').pipe(
				map((response) => {
					const mapper = new UploadBatchResourceToUploadMapper();
					return mapper.map(response);
				})
			);
		}

		const request = new ODataRequest('get', 'uploadbatches');
		request.$count();
		if (params?.take) {
			request.$top(params?.take);
		}

		if (params?.skip) {
			request.$skip(params?.skip);
		}

		if (params?.orderByColumn && params?.orderByDirection) {
			request.$orderBy(params?.orderByColumn, params?.orderByDirection);
		}

		return this.makeODataRequest$<PagingResponse<UploadBatchResource>>(request, 'api2').pipe(
			map((response) => {
				const mapper = new UploadBatchResourceToUploadMapper();
				return new Collection<Upload>({
					items: mapper.mapCollection(response.value),
					totalItems: response.count
				});
			})
		);
	}

	public put(params: PutParams): Observable<Upload> {
		if (!params?.upload) return of(null);

		const mapper = new UploadToUploadBatchResourceMapper();
		const body = mapper.map(params.upload);
		const request = new ODataRequest('put', 'uploadbatches', params.upload.id, body);
		return this.makeODataRequest$<UploadBatchResource>(request, 'api2').pipe(
			map((response) => {
				const mapper = new UploadBatchResourceToUploadMapper();
				return mapper.map(response);
			})
		);
	}

	public post(params: PostParams): Observable<Upload> {
		if (!params?.upload) return of(null);

		const mapper = new UploadToUploadBatchResourceMapper();
		const body = mapper.map(params.upload);
		const request = new ODataRequest('post', 'uploadbatches', body);
		return this.makeODataRequest$<UploadBatchResource>(request, 'api2').pipe(
			map((response) => {
				const mapper = new UploadBatchResourceToUploadMapper();
				return mapper.map(response);
			})
		);
	}

	public delete(params: DeleteParams): Observable<void> {
		if (!params?.ids) return of(null);


		return forkJoin(params?.ids.map((id) => {
			const request = new ODataRequest('delete', 'uploadbatches', id);
			return this.makeODataRequest$(request, 'api2');
		})).pipe(
			map(() => null)
		);
	}

	public validate(uploadBatchIds: string[]): Observable<void> {
		return this.makeHttpRequest('post', 'api2/uploadbatches/validate', uploadBatchIds);
	}

	CompleteBatch(uploadBatchId: string): Observable<void> {
		return this.makeHttpRequest('post', `api2/uploadBatches/${uploadBatchId}/complete`, null);
	}
}

interface GetParams {
	id?: string;
	skip?: number;
	take?: number;
	orderByColumn?: string;
	orderByDirection?: 'asc' | 'desc';
}

interface PutParams {
	upload: Upload;
}

interface PostParams {
	upload: Upload;
}

interface DeleteParams {
	ids: string[];
}
